import { lazy, startTransition, Suspense, useActionState, useEffect, useState } from "react";
import { api, ApiError } from "../api.js";
import { useI18n } from "../useI18n.js";
import { Logo } from "../ui/Icon.js";
import type { RecoveryNotice } from "../shell/Banners.js";
// 再設定の画面は押したときにだけ読む（ログイン画面の初回の読み込みを軽くする）
const PasswordReset = lazy(() => import("./PasswordReset.js").then((m) => ({ default: m.PasswordReset })));

type LoginState = { error: "network" | "credentials" | "totpInvalid" | null; needTotp: boolean; totpPrompt: boolean };

export function Login({ onSignedIn, notice }: { onSignedIn: (recovery?: RecoveryNotice) => void; notice?: string | null }) {
  const { t, lang, setLang } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [missing, setMissing] = useState(false);
  // H-10: コードでパスワードを再設定する面。成功したらメールアドレスを残してログインに戻る
  const [mode, setMode] = useState<"login" | "reset">("login");
  const [resetDone, setResetDone] = useState(false);
  const [review, setReview] = useState<{ reviewMode: boolean; label: string | null } | null>(null);
  const [providers, setProviders] = useState<Array<{ code: string; displayName: string }>>([]);
  // OIDC のコールバックが理由付きで戻ってくる（#/login?oidc=NOT_PROVISIONED 等）
  const oidcResult = new URLSearchParams(location.hash.split("?")[1] ?? "").get("oidc");

  // レビュー配備かどうかだけを未認証で取る（防御の詳細は出さない）
  useEffect(() => {
    api.get<{ reviewMode: boolean; label: string | null }>("/deployment")
      .then(setReview).catch(() => setReview(null));
    api.get<{ providers: Array<{ code: string; displayName: string }> }>("/auth/oidc/providers")
      .then((r) => setProviders(r.providers)).catch(() => setProviders([]));
  }, []);

  // ⛔ここにパスワードポリシーを出さないこと。
  // 最小長とロック閾値を未認証で確定させると、閾値を踏まない総当たり
  // （1アカウントあたり threshold-1 回のパスワードスプレー）が組める。
  // 要件の提示はパスワードを「設定する」画面の役目で、ログイン画面では不要。
  // 4-1-3-4 / 4-1-5-3 の証跡は認証後の設定画面と申請パッケージ出力で見せる。

  // React 19 の useActionState: 送信中・失敗の扱いをここにまとめる。入力の state には触らない（消さない）。
  const [state, dispatch, pending] = useActionState<LoginState, void>(async (prev) => {
    try {
      const r = await api.post<{ ok: true; recoveryNotice?: RecoveryNotice }>("/auth/login", { email, password, ...(totpCode ? { totpCode } : {}) });
      // review-4 M2-c: 復旧の操作の後の最初のログインなら、サーバが一度だけ知らせを返す
      onSignedIn(r?.recoveryNotice);
      return { error: null, needTotp: prev.needTotp, totpPrompt: false };
    } catch (err) {
      // UX M-36: サーバ障害・通信断は「パスワードが違う」とは別の文言にする。
      // ロックの有無や回数には触れない（未認証の画面に防御の詳細を出さない方針）。
      if (!(err instanceof ApiError) || err.status === 0 || err.status >= 500) return { ...prev, error: "network", totpPrompt: false };
      // UX L-07: 「コードを入力してください」はエラーではなく案内。実際に違ったとき（TOTP_INVALID）だけ赤で出す。
      if (err.code === "TOTP_REQUIRED") return { error: null, needTotp: true, totpPrompt: true };
      if (err.code === "TOTP_INVALID") return { error: "totpInvalid", needTotp: true, totpPrompt: false };
      // ⛔ロック・アカウント無効を個別に出さない（存在と状態が判ってしまう）。
      // 実際の理由は監査ログに残る。利用者にはサポート窓口を案内する。
      return { ...prev, error: "credentials", totpPrompt: false };
    }
  }, { error: null, needTotp: false, totpPrompt: false });

  const errorMsg = missing ? t("login.err.missing")
    : state.error === "network" ? t("login.err.network")
    : state.error === "totpInvalid" ? t("login.err.totpInvalid")
    : state.error === "credentials" ? t("login.err.credentials") : null;
  const oidcMsg = oidcResult
    ? (t(`login.oidc.${oidcResult}`) === `login.oidc.${oidcResult}` ? t("login.oidc.FAILED") : t(`login.oidc.${oidcResult}`))
    : null;

  if (mode === "reset") {
    return (
      <div className="login-wrap">
        <main id="main">
          <Suspense fallback={<p className="muted" role="status">{t("common.loading")}</p>}>
          <PasswordReset initialEmail={email} onBack={() => setMode("login")}
                         onDone={(e) => { setEmail(e); setPassword(""); setTotpCode(""); setResetDone(true); setMode("login"); }}
                         header={<>
                           <div className="login-head"><Logo size={36} /><h1>{t("login.title")}</h1></div>
                           <LangSwitch lang={lang} setLang={setLang} />
                         </>} />
          </Suspense>
        </main>
      </div>
    );
  }

  return (
    <div className="login-wrap">
      <main id="main">
        <form className="login" noValidate onSubmit={(e) => {
          e.preventDefault();
          if (!email || !password) { setMissing(true); document.getElementById(!email ? "email" : "pw")?.focus(); return; }
          setMissing(false);
          startTransition(() => dispatch());
        }}>
          <div className="login-head">
            <Logo size={36} />
            <h1>{t("login.title")}</h1>
          </div>
          <LangSwitch lang={lang} setLang={setLang} />

          {resetDone && <p className="callout ok" role="status">{t("pwr.done")}</p>}
          {notice && <p className="callout warn" role="status">{notice}</p>}

          {review?.reviewMode && (
            <div className="callout review">
              <p className="bold">{t("review.heading")}</p>
              <p>{t("review.body")}</p>
              {review.label && <p className="muted">{t("review.deployment")}: {review.label}</p>}
            </div>
          )}

          {oidcMsg && <p className="callout danger" role="alert">{oidcMsg}</p>}
          {providers.length > 0 && (
            <div className="sso">
              {providers.map((p) => (
                <a key={p.code} className="btn" href={api.url(`/auth/oidc/${p.code}/start`)}>{t("login.sso", { name: p.displayName })}</a>
              ))}
              <p className="muted small">{t("login.ssoOr")}</p>
            </div>
          )}

          <div className="field">
            <label htmlFor="email">{t("login.email")}</label>
            <input id="email" type="email" value={email} autoComplete="username" inputMode="email" autoCapitalize="none" spellCheck={false}
                   aria-describedby={errorMsg ? "login-err" : undefined} aria-invalid={state.error === "credentials" || undefined}
                   onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="pw">{t("login.password")}</label>
            <input id="pw" type="password" value={password} autoComplete="current-password"
                   autoFocus={resetDone}
                   aria-describedby={errorMsg ? "login-err" : undefined} aria-invalid={state.error === "credentials" || undefined}
                   onChange={(e) => setPassword(e.target.value)} />
          </div>
          {state.needTotp && (
            <div className="field">
              <label htmlFor="totp">{t("login.totp")}</label>
              {state.totpPrompt && <p className="field-hint" id="totp-hint" role="status">{t("login.totpPrompt")}</p>}
              <input id="totp" inputMode="numeric" value={totpCode} autoComplete="one-time-code" maxLength={6}
                     aria-describedby={state.totpPrompt ? "totp-hint" : errorMsg ? "login-err" : undefined}
                     aria-invalid={state.error === "totpInvalid" || undefined}
                     /* 第2段で出る唯一の入力欄なので、ここだけ自動でフォーカスする */
                     autoFocus onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))} />
            </div>
          )}
          {errorMsg && <p className="error-panel ep-title" id="login-err" role="alert">{errorMsg}</p>}
          <button className="primary" type="submit" disabled={pending}>
            {pending ? t("login.submitting") : t("login.submit")}
          </button>
          <p className="login-alt">
            <button type="button" className="quiet" onClick={() => { setResetDone(false); setMode("reset"); }}>{t("pwr.link")}</button>
          </p>
        </form>
      </main>
    </div>
  );
}

/** 言語切替。ログイン前から使えないと、英語話者は最初の画面で詰まる。 */
export function LangSwitch({ lang, setLang }: { lang: string; setLang: (l: "ja" | "en") => void }) {
  // L-04: グループ名が英語固定で、選択中かどうかも伝わっていなかった
  const label = lang === "ja" ? "表示言語" : "Language";
  return (
    <span className="langswitch" role="group" aria-label={label}>
      {(["ja", "en"] as const).map((l) => (
        <button key={l} type="button" aria-pressed={lang === l} lang={l} onClick={() => setLang(l)}>
          {l === "ja" ? "日本語" : "English"}
        </button>
      ))}
    </span>
  );
}
